import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, FolderKanban, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/Toast';
import Select from 'react-select';

const STATUS_BADGE_STYLES = {
  Draft: { bg: '#f8fafc', color: '#475569', dot: '#64748b' },
  'In Collaboration': { bg: '#fef3c7', color: '#b45309', dot: '#b45309' },
  Closed: { bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
};

const renderStatusBadge = (status) => {
  const label = status || 'Draft';
  const styleConfig = STATUS_BADGE_STYLES[label] || STATUS_BADGE_STYLES.Draft;
  return (
    <span
      className="badge-premium"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 14px',
        borderRadius: '50px',
        fontSize: '12px',
        fontWeight: 600,
        background: styleConfig.bg,
        color: styleConfig.color,
        border: `1px solid ${styleConfig.color}30`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: styleConfig.dot,
        }}
      />
      {label}
    </span>
  );
};

const notMovedToPreRfp = (p) => p.dprStatus !== 'Moved to Pre-RFP';

const hasInitiationProgress = (p) =>
  Boolean(
    p.problemStatement ||
    p.department ||
    p.projectType ||
    (p.contributions && p.contributions.length > 0) ||
    (p.requirements && p.requirements.length > 0),
  );

const PHASE_CONFIG = {
  initiation: {
    title: 'Project Initiation',
    description:
      'Kickoff the project, fill structured fields, invite contributors — AI analyses all inputs continuously.',
    showCreate: true,
    workspacePath: (id) => `/sourcing/project-intelligence/initiation/${id}`,
    emptyTitle: 'No projects found',
    emptyMessage: 'Create a new project to start the initiation phase.',
    filter: notMovedToPreRfp,
  },
  builder: {
    title: 'DPR Builder',
    description:
      'Select a project to draft DPR sections with AI from the project brief.',
    showCreate: false,
    workspacePath: (id) => `/sourcing/project-intelligence/builder/${id}`,
    emptyTitle: 'No eligible projects',
    emptyMessage:
      'Complete project initiation first, then return here to build the DPR.',
    filter: (p) => notMovedToPreRfp(p) && hasInitiationProgress(p),
  },
  regulatory: {
    title: 'Regulatory Approvals',
    description:
      'Select a project to track regulatory clearances, uploads, and due dates.',
    showCreate: false,
    workspacePath: (id) => `/sourcing/project-intelligence/regulatory/${id}`,
    emptyTitle: 'No eligible projects',
    emptyMessage:
      'Start or complete DPR sections before tracking regulatory approvals.',
    filter: (p) =>
      notMovedToPreRfp(p) &&
      ((p.dprSections && p.dprSections.length > 0) ||
        p.dprStatus === 'In Progress' ||
        p.dprStatus === 'Completed' ||
        (p.compliances && p.compliances.length > 0)),
  },
  finalisation: {
    title: 'DPR Finalisation',
    description:
      'Select a project to consolidate the DPR and submit for final approval.',
    showCreate: false,
    workspacePath: (id) => `/sourcing/project-intelligence/finalisation/${id}`,
    emptyTitle: 'No eligible projects',
    emptyMessage:
      'Projects with DPR work in progress or completed will appear here.',
    filter: (p) =>
      notMovedToPreRfp(p) &&
      (p.dprStatus === 'In Progress' ||
        p.dprStatus === 'Completed' ||
        (p.dprSections && p.dprSections.length > 0)),
  },
  legacy: {
    title: 'Project Requirements',
    description:
      'Collaborative workspace for tenant members to define and refine technical and business requirements.',
    showCreate: true,
    workspacePath: (id) => `/sourcing/requirement-gathering/project/${id}`,
    emptyTitle: 'No Project Requirements Found',
    emptyMessage: 'Create a new project requirement to start collaborating.',
    filter: () => true,
  },
};

const resolvePhase = (pathname) => {
  if (pathname.includes('/project-intelligence/initiation')) return 'initiation';
  if (pathname.includes('/project-intelligence/builder')) return 'builder';
  if (pathname.includes('/project-intelligence/regulatory')) return 'regulatory';
  if (pathname.includes('/project-intelligence/finalisation')) return 'finalisation';
  return 'legacy';
};

const ProjectRequirementsLanding = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { success, error } = useToast();

  const phase = resolvePhase(location.pathname);
  const phaseConfig = PHASE_CONFIG[phase];

  const [showModal, setShowModal] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [newProject, setNewProject] = useState({
    projectTitle: '',
    projectDescription: '',
    category: '',
    collaborators: [],
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      if (res.data.success && res.data.data) {
        setTenantUsers(
          res.data.data.map((u) => ({
            value: u.id || u._id,
            label: `${u.firstName || u.name || ''} ${u.lastName || ''}`.trim(),
          })),
        );
      }
    } catch (err) {
      console.warn('Could not fetch users', err);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/sourcing/project-requirements');
      if (res.data.success) {
        setProjects(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching project requirements:', err);
      error('Failed to load project requirements.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = async () => {
    if (!newProject.projectTitle.trim()) {
      return error('Project Title is required');
    }

    try {
      setCreating(true);
      const res = await api.post('/sourcing/project-requirements', {
        projectTitle: newProject.projectTitle,
        projectDescription: newProject.projectDescription,
        category: newProject.category,
      });

      if (res.data.success) {
        const projectId = res.data.data._id;

        if (newProject.collaborators.length > 0) {
          const collabs = newProject.collaborators.map((c) => c.value);
          await api.put(`/sourcing/project-requirements/${projectId}/collaborators`, {
            collaborators: collabs,
          });
        }

        success('Project created successfully!');
        setShowModal(false);
        setNewProject({
          projectTitle: '',
          projectDescription: '',
          category: '',
          collaborators: [],
        });

        const targetPath =
          phase === 'legacy'
            ? `/sourcing/requirement-gathering/project/${projectId}`
            : `/sourcing/project-intelligence/initiation/${projectId}`;
        navigate(targetPath);
      }
    } catch (err) {
      console.error('Error creating project:', err);
      error('Failed to create a new project.');
    } finally {
      setCreating(false);
    }
  };

  const tableData = useMemo(
    () => projects.filter(phaseConfig.filter),
    [projects, phaseConfig],
  );

  const openProject = useCallback(
    (project) => {
      navigate(phaseConfig.workspacePath(project._id));
    },
    [navigate, phaseConfig],
  );

  const columns = useMemo(
    () => [
      {
        key: 'projectTitle',
        label: 'Project Title',
        width: '30%',
        render: (_, row) => (
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 0, maxWidth: '320px' }}
          >
            <FolderKanban size={16} style={{ color: 'var(--primary-color)', flexShrink: 0 }} />
            <span
              title={row.projectTitle || ''}
              style={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {row.projectTitle || '-'}
            </span>
          </div>
        ),
      },
      {
        key: 'category',
        label: 'Category',
        width: '140px',
        render: (value) => value || 'N/A',
      },
      {
        key: 'status',
        label: 'Status',
        width: '160px',
        render: (_, row) => renderStatusBadge(row.status),
      },
      {
        key: 'dprStatus',
        label: 'DPR Status',
        width: '140px',
        render: (value) => value || 'Not Initiated',
      },
      {
        key: 'owner',
        label: 'Owner',
        width: '140px',
        render: (_, row) =>
          row.owner
            ? `${row.owner.firstName || ''} ${row.owner.lastName || ''}`.trim() || 'Unknown'
            : 'Unknown',
      },
      {
        key: 'myRole',
        label: 'My Role',
        width: '120px',
        render: (_, row) => {
          const isOwner = row.owner?._id === user?._id || row.owner?.id === user?._id;
          return isOwner ? (
            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
              Owner
            </span>
          ) : (
            <span className="badge bg-info-subtle text-info border border-info-subtle">
              Collaborator
            </span>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Created',
        width: '110px',
        render: (_, row) =>
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-',
      },
      {
        key: '_open',
        label: '',
        width: '48px',
        render: () => <ChevronRight size={16} style={{ color: '#9ca3af' }} />,
      },
    ],
    [user],
  );

  const headerActions = phaseConfig.showCreate
    ? [
        {
          label: 'Create Project',
          icon: <Plus size={18} />,
          variant: 'primary',
          onClick: () => setShowModal(true),
        },
      ]
    : [];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={phaseConfig.title}
        description={phaseConfig.description}
        actions={headerActions}
      />

      <div className="page-container">
        <DataTable
          data={tableData}
          columns={columns}
          title="Projects"
          loading={loading}
          showActions={false}
          searchable
          sortable
          exportable={false}
          showFilterButton={false}
          pagination
          pageSize={10}
          onRowClick={openProject}
          searchPlaceholder="Search projects by name or status..."
          emptyMessage={
            <div className="text-center py-4">
              <FolderKanban size={40} className="text-muted opacity-50 mb-3" />
              <h5 className="text-dark fw-bold mb-2">{phaseConfig.emptyTitle}</h5>
              <p className="text-muted mb-0">{phaseConfig.emptyMessage}</p>
              {phaseConfig.showCreate && (
                <button
                  type="button"
                  className="btn btn-primary mt-3 d-inline-flex align-items-center gap-2"
                  onClick={() => setShowModal(true)}
                >
                  <Plus size={16} />
                  Create Project
                </button>
              )}
            </div>
          }
          className="shadow-sm border-0"
        />
      </div>

      {showModal &&
        createPortal(
          <div
            className="modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={() => setShowModal(false)}
            role="presentation"
          >
            <div
              className="modal"
              style={{ width: '600px', maxWidth: '90vw' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-project-modal-title"
            >
              <div className="modal-header">
                <h5 id="create-project-modal-title" className="modal-title fw-bold mb-0">
                  Create New Project
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="mb-3">
                  <label className="form-label fw-medium">
                    Project Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter project title"
                    value={newProject.projectTitle}
                    onChange={(e) =>
                      setNewProject({ ...newProject, projectTitle: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-medium">Project Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Briefly describe the goals and scope"
                    value={newProject.projectDescription}
                    onChange={(e) =>
                      setNewProject({ ...newProject, projectDescription: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-medium">Category</label>
                  <select
                    className="form-select"
                    value={newProject.category}
                    onChange={(e) =>
                      setNewProject({ ...newProject, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    <option value="IT Software">IT Software</option>
                    <option value="Construction">Construction</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label fw-medium">Invite Collaborators</label>
                  <Select
                    isMulti
                    name="collaborators"
                    options={tenantUsers.filter(
                      (u) => u.value !== user?.id && u.value !== user?._id,
                    )}
                    classNamePrefix="select"
                    placeholder="Search users to collaborate..."
                    value={newProject.collaborators}
                    onChange={(selected) =>
                      setNewProject({ ...newProject, collaborators: selected || [] })
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 38,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 12000 }),
                    }}
                  />
                  <div className="form-text">
                    These users will be able to add requirements and upload documents.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateNew}
                  disabled={creating || !newProject.projectTitle.trim()}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ProjectRequirementsLanding;
